import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaFileLines, FaBriefcase, FaChartBar, FaPlus, FaEye, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import Breadcrumb from '../../../components/Breadcrumb';
import api from '../../../api';

export default function Dashboard() {
  const [stats, setStats] = useState({
    users: 0,
    assessments: 0,
    jobs: 0,
    results: 0
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch users count
      const usersResponse = await api.get('/users');
      const usersCount = usersResponse.data.length;
      
      setStats({
        users: usersCount,
        assessments: 12, // Mock data
        jobs: 8,   // Mock data
        results: 45 // Mock data
      });
      
      // Mock recent activity
      setRecentActivity([
        {
          icon: 'fa-user-plus',
          text: 'New user Mohamed Ahmed registered',
          time: '2 mins ago',
          type: 'success'
        },
        {
          icon: 'fa-file-plus',
          text: 'Exam "React Developer Test" created',
          time: '15 mins ago',
          type: 'info'
        },
        {
          icon: 'fa-chart-line',
          text: 'Results exported for Job "Frontend Developer"',
          time: '1 hour ago',
          type: 'primary'
        },
        {
          icon: 'fa-user-tie',
          text: 'New candidate application received',
          time: '2 hours ago',
          type: 'success'
        }
      ]);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue, linkTo }) => (
    <div className="stat-card" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)` }}>
      <div className="stat-card-content">
        <div className="stat-icon">
          <i className={`fa-solid ${icon}`}></i>
        </div>
        <div className="stat-details">
          <h3 className="stat-value">{loading ? '...' : value}</h3>
          <p className="stat-title">{title}</p>
          {trend && (
            <div className={`stat-trend ${trend}`}>
              <i className={`fa-solid fa-arrow-${trend === 'up' ? 'up' : 'down'}`}></i>
              <span>{trendValue}% from last month</span>
            </div>
          )}
        </div>
        <Link to={linkTo} className="stat-link">
          <FaEye />
        </Link>
      </div>
    </div>
  );

  const ActivityItem = ({ activity }) => (
    <div className="activity-item">
      <div className={`activity-icon activity-${activity.type}`}>
        <i className={`fa-solid ${activity.icon}`}></i>
      </div>
      <div className="activity-content">
        <p className="activity-text">{activity.text}</p>
        <span className="activity-time">{activity.time}</span>
      </div>
    </div>
  );

  const QuickAction = ({ title, description, icon, color, linkTo }) => (
    <Link to={linkTo} className="quick-action" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="quick-action-icon" style={{ background: `${color}20`, color: color }}>
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div className="quick-action-content">
        <h6 className="quick-action-title">{title}</h6>
        <p className="quick-action-description">{description}</p>
      </div>
    </Link>
  );

  return (
    <div className="main-content flex-grow-1 page-transition" style={{ 
      display: "flex",
      flexDirection: "column",
      padding: "0 1.5rem", 
      margin: 0, 
      minHeight: 0,
      background: "#f8f9ff"
    }}>
      <Breadcrumb items={[
        {label: 'Dashboard'}
      ]} />
      
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          <i className="fa-solid fa-gauge me-3"></i>
          Dashboard
        </h1>
        <p className="dashboard-subtitle">Welcome back! Here's what's happening with your recruitment system.</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          title="Total Users"
          value={stats.users}
          icon="fa-users"
          color="#29204C"
          trend="up"
          trendValue="12"
          linkTo="/users"
        />
        <StatCard
          title="Assessments"
          value={stats.assessments}
          icon="fa-clipboard-list"
          color="#68B977"
          trend="up"
          trendValue="8"
          linkTo="/assessments"
        />
        <StatCard
          title="Open Jobs"
          value={stats.jobs}
          icon="fa-briefcase"
          color="#5A6D8B"
          trend="down"
          trendValue="3"
          linkTo="/jobs"
        />
        <StatCard
          title="Total Results"
          value={stats.results}
          icon="fa-chart-bar"
          color="#F59E0B"
          trend="up"
          trendValue="15"
          linkTo="/results"
        />
      </div>

      <div className="dashboard-content">
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="card-header">
            <h5 className="card-title">
              <i className="fa-solid fa-clock me-2"></i>
              Recent Activity
            </h5>
            <Link to="/logs" className="view-all-link">View All</Link>
          </div>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
              <ActivityItem key={index} activity={activity} />
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card">
          <div className="card-header">
            <h5 className="card-title">
              <i className="fa-solid fa-bolt me-2"></i>
              Quick Actions
            </h5>
          </div>
          <div className="quick-actions-grid">
            <QuickAction
              title="Add New User"
              description="Create a new user account"
              icon="fa-user-plus"
              color="#29204C"
              linkTo="/users"
            />
            <QuickAction
              title="Create Assessment"
              description="Set up a new assessment"
              icon="fa-plus"
              color="#68B977"
              linkTo="/assessments"
            />
            <QuickAction
              title="View Reports"
              description="Check recruitment analytics"
              icon="fa-chart-line"
              color="#5A6D8B"
              linkTo="/results"
            />
          </div>
        </div>
      </div>
    </div>
  );
} 